import React from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { TeamsAppAnalyticsContext } from '@atlaskit/teams-app-internal-analytics';
import { TeamsNavigationProvider } from '@atlaskit/teams-app-internal-navigation';
import { useTeamsSentryClientSetup } from '@atlaskit/teams-client/sentry';
import { DialogProvider } from '@atlassian/people-teams-ui/dialogs';

import { useSetAppConfig } from '../app-config-context/hooks/use-set-teams-app-config';
import { AppConfigProvider } from '../app-config-context/provider';
import { type AppConfigProviderProps, type AppConfigStoreState } from '../app-config-context/types';
import { ProfileCardClientConfigProvider } from '../profile-client/provider';

const IS_LOCAL = process.env.NODE_ENV === 'development';

/**
 * This component is intended to wrap all exported components from @atlassian/teams
 * It handles configuration & setup of the underlying clients.
 */
export function TeamsWrapper({
	children,
	appConfig,
}: {
	children: React.ReactNode;
	appConfig: AppConfigStoreState & AppConfigProviderProps;
}): React.JSX.Element | null {
	useTeamsSentryClientSetup(!IS_LOCAL, {
		env: appConfig.env,
		appName: appConfig.appName ? `${appConfig.appName}` : undefined,
	});

	const { isClientReady } = useSetAppConfig(appConfig);

	// Don't render children until the teams client is properly configured
	// Otherwise we make fetch requests to the client before it has context set
	if (!isClientReady && fg('team-profile-await-client-readiness')) {
		return null;
	}

	return (
		<AppConfigProvider {...appConfig}>
			<ProfileCardClientConfigProvider {...appConfig}>
				<TeamsAppAnalyticsContext>
					<TeamsNavigationProvider
						value={{
							forceExternalIntent: appConfig.openLinksInNewTab ?? false,
							navigate: appConfig.push,
							openPreviewPanel: appConfig.openPreviewPanel,
							contextEntryPoint: appConfig.contextEntryPoint ?? undefined,
						}}
					>
						<DialogProvider>{children}</DialogProvider>
					</TeamsNavigationProvider>
				</TeamsAppAnalyticsContext>
			</ProfileCardClientConfigProvider>
		</AppConfigProvider>
	);
}
